// Shared building blocks for marketing pages. Styles live in css/theme.css.
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BiSolidZap } from "react-icons/bi";
import { Helmet } from "react-helmet-async";
import { getBaseUrl } from "../SEO/SEOHead";
import LandingHeadline from "./LandingHeadline";

// Internal paths use client-side routing; anything absolute opens in a new tab.
export function SmartLink({ to, children, className, ...rest }) {
  if (/^(https?:|mailto:)/.test(to) || to.endsWith(".pdf")) {
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className} {...rest}>
      {children}
    </Link>
  );
}

export function Button({ to, variant = "primary", children, icon = false }) {
  return (
    <SmartLink to={to} className={`ft-btn ft-btn--${variant}`}>
      {children}
      {icon && <ArrowRight size={18} aria-hidden="true" />}
    </SmartLink>
  );
}

// Amber "BETA" pill, matching the product's BetaBadge (frothtestops-frontend-revamp).
// Pass `dark` on dark surfaces.
export function BetaBadge({ title = "This feature is in beta", dark = false }) {
  return (
    <span className={`ft-badge ft-badge--beta${dark ? " is-dark" : ""}`} title={title}>
      <BiSolidZap size={11} aria-hidden="true" />
      BETA
    </span>
  );
}

export function NewBadge() {
  return <span className="ft-badge ft-badge--new">New</span>;
}

// align: "split" (default; title left, text right), "left" or "center".
export function SectionHeading({ eyebrow, title, subtitle, align = "split", more, as: Tag = "h2" }) {
  if (align === "split") {
    return (
      <div className="ft-section-head ft-section-head--split">
        <div>
          {eyebrow && <span className="ft-eyebrow">{eyebrow}</span>}
          <Tag className="ft-heading">{title}</Tag>
        </div>
        {(subtitle || more) && (
          <div className="ft-section-head__aside">
            {subtitle && <p className="ft-lead">{subtitle}</p>}
            {more && (
              <SmartLink to={more.to} className="ft-more">
                {more.label} <ArrowRight size={16} aria-hidden="true" />
              </SmartLink>
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={`ft-section-head ${align === "left" ? "ft-section-head--left" : ""}`}>
      {eyebrow && <span className="ft-eyebrow">{eyebrow}</span>}
      <Tag className="ft-heading">{title}</Tag>
      {subtitle && <p className="ft-lead mb-0">{subtitle}</p>}
    </div>
  );
}

// Product screenshots are exported at 1800px wide; pass the real height so the browser reserves space.
export function ScreenshotFrame({ src, alt, url = "app.frothtestops.com", className = "", width = 1800, height, priority = false }) {
  return (
    <figure className={`ft-frame m-0 ${className}`}>
      <div className="ft-frame__bar" aria-hidden="true">
        <span />
        <span />
        <span />
        <div className="ft-frame__url">{url}</div>
      </div>
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        {...(priority ? { fetchPriority: "high" } : {})}
      />
    </figure>
  );
}

export function FeatureCard({ icon: Icon, title, text, badge, link, accent = false, className = "", children }) {
  return (
    <div className={`ft-card ${className}`}>
      {Icon && (
        <div className={`ft-card__icon ${accent ? "ft-card__icon--accent" : ""}`}>
          <Icon size={24} aria-hidden="true" />
        </div>
      )}
      <h3 className="ft-card__title">
        {title} {badge}
      </h3>
      <p className="ft-card__text">{text}</p>
      {link && (
        <SmartLink to={link.to} className="ft-card__link">
          {link.label} <ArrowRight size={16} aria-hidden="true" />
        </SmartLink>
      )}
      {children}
    </div>
  );
}

export function CtaBanner({
  title = "See Froth TestOps in action",
  text = "Talk to our team about your testing workflow and we'll show you how Froth TestOps fits it.",
  primary = { to: "/contact", label: "Talk to Us" },
  secondary = { to: "/froth-ai", label: "Explore FrothAI" },
}) {
  return (
    <section className="ft-section ft-section--tight">
      <div className="ft-container">
        <div className="ft-cta">
          <h2>{title}</h2>
          <p>{text}</p>
          <div className="ft-btn-row">
            <Button to={primary.to} icon>
              {primary.label}
            </Button>
            {secondary && (
              <Button to={secondary.to} variant="ghost">
                {secondary.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// BreadcrumbList structured data (Home > page) for search results.
export function BreadcrumbSchema({ name }) {
  const origin = getBaseUrl();
  const path = useLocation().pathname.toLowerCase();
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name, item: `${origin}${path}` },
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export function PageHero({ eyebrow, title, lead, crumbs, actions, children, visual }) {
  const copy = (
    <div className="ft-page-hero__copy">
      {crumbs && (
        <nav className="ft-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{crumbs}</span>
        </nav>
      )}
      {eyebrow && <span className="ft-eyebrow">{eyebrow}</span>}
      <LandingHeadline className="ft-display">{title}</LandingHeadline>
      {lead && <p className="ft-lead">{lead}</p>}
      {actions && <div className="ft-btn-row">{actions}</div>}
      {children}
    </div>
  );
  return (
    <section className={`ft-page-hero ${visual ? "ft-page-hero--split" : ""}`}>
      {crumbs && typeof crumbs === "string" && <BreadcrumbSchema name={crumbs} />}
      <div className="ft-container">
        {visual ? (
          <div className="ft-page-hero__grid">
            {copy}
            <div className="ft-page-hero__visual">{visual}</div>
          </div>
        ) : (
          copy
        )}
      </div>
    </section>
  );
}

// Page-hero visual: a main product screenshot with a smaller one overlapping its corner.
export function HeroShots({ main, mainAlt, float, floatAlt, mainHeight, floatHeight }) {
  return (
    <div className="ft-hero-shots">
      {/* Heights reserve the aspect ratio so late image loads can't shift the
          layout under scrollspy / deep-link positioning. */}
      <ScreenshotFrame src={main} alt={mainAlt} height={mainHeight} />
      {float && (
        <ScreenshotFrame className="ft-hero-shots__float" src={float} alt={floatAlt} height={floatHeight} />
      )}
    </div>
  );
}

// A phone outline around a mobile-app screenshot.
export function PhoneMock({ src, alt, className = "" }) {
  return (
    <figure className={`ft-phone m-0 ${className}`}>
      <span className="ft-phone__notch" aria-hidden="true" />
      <img src={src} alt={alt} loading="lazy" />
    </figure>
  );
}
